import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Flower2, ArrowLeft, Award, CheckCircle, XCircle, Flame, Star, Sparkles, Heart, Trophy } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { quizAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Messages encourageants pour les bonnes réponses
const CORRECT_MESSAGES = [
  "Bravo ! Tu gères ! 💪",
  "Excellente réponse ! 🌟",
  "Tu es sur la bonne voie ! ✨",
  "Génial ! Continue comme ça ! 🎉",
  "Parfait ! Tu maîtrises ! 👏",
  "Super ! Tu es brillante ! 💫",
  "Wow ! Quelle réponse ! 🔥",
  "Incroyable ! Tu assures ! 🌺"
];

// Messages encourageants pour les mauvaises réponses
const INCORRECT_MESSAGES = [
  "Pas grave, tu apprends ! 💜",
  "Continue, tu vas y arriver ! 🌸",
  "L'important c'est d'essayer ! 💪",
  "Ne lâche rien ! 🌟",
  "C'est en se trompant qu'on apprend ! 📚"
];

// Messages pour les streaks
const STREAK_MESSAGES = {
  3: "🔥 Série de 3 ! Tu es en feu !",
  5: "⭐ Série de 5 ! Impressionnant !",
  7: "🏆 Série de 7 ! Tu es inarrêtable !",
  10: "👑 Série de 10 ! Tu es une championne !"
};

const LessonQuizEnhanced = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { updateUser, user } = useAuth();
  
  const [quiz, setQuiz] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [lesson, setLesson] = useState(null);
  
  // États pour les nouvelles fonctionnalités
  const [streak, setStreak] = useState(0);
  const [maxStreak, setMaxStreak] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [lastAnswerCorrect, setLastAnswerCorrect] = useState(null);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [streakMessage, setStreakMessage] = useState('');
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [bonusPoints, setBonusPoints] = useState(0);

  useEffect(() => {
    const fetchQuiz = async () => {
      try {
        const token = localStorage.getItem('token');
        
        const lessonResponse = await fetch(`${BACKEND_URL}/api/lessons/${lessonId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const lessonData = await lessonResponse.json();
        setLesson(lessonData.lesson);
        
        const quizResponse = await fetch(`${BACKEND_URL}/api/lessons/${lessonId}/quiz`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const quizData = await quizResponse.json();
        setQuiz(quizData.quiz);
        setSelectedAnswers(new Array(quizData.quiz.questions.length).fill(-1));
        setAnsweredQuestions(new Array(quizData.quiz.questions.length).fill(false));
      } catch (error) {
        console.error('Error fetching quiz:', error);
        toast({
          title: 'Erreur',
          description: 'Impossible de charger le quiz',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchQuiz();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId]);

  const handleSelectAnswer = (optionIndex) => {
    if (answeredQuestions[currentQuestion]) return; // Empêcher de changer après avoir répondu
    
    const newAnswers = [...selectedAnswers];
    newAnswers[currentQuestion] = optionIndex;
    setSelectedAnswers(newAnswers);
  };

  const handleValidateAnswer = useCallback(() => {
    if (selectedAnswers[currentQuestion] === -1) return;
    
    const question = quiz.questions[currentQuestion];
    const selectedOption = question.options[selectedAnswers[currentQuestion]];
    const isCorrect = selectedOption.isCorrect;
    
    // Marquer comme répondu
    const newAnswered = [...answeredQuestions];
    newAnswered[currentQuestion] = true;
    setAnsweredQuestions(newAnswered);
    
    // Gérer le streak
    if (isCorrect) {
      const newStreak = streak + 1;
      setStreak(newStreak);
      if (newStreak > maxStreak) setMaxStreak(newStreak);
      
      // Bonus de streak
      if (newStreak >= 3) {
        const streakBonus = Math.floor(newStreak * 2);
        setBonusPoints(prev => prev + streakBonus);
      }
      
      // Message de streak
      if (STREAK_MESSAGES[newStreak]) {
        setStreakMessage(STREAK_MESSAGES[newStreak]);
      } else {
        setStreakMessage('');
      }
      
      setFeedbackMessage(CORRECT_MESSAGES[Math.floor(Math.random() * CORRECT_MESSAGES.length)]);
    } else {
      setStreak(0);
      setStreakMessage('');
      setFeedbackMessage(INCORRECT_MESSAGES[Math.floor(Math.random() * INCORRECT_MESSAGES.length)]);
    }
    
    setLastAnswerCorrect(isCorrect);
    setShowFeedback(true);
  // CORRECT_MESSAGES, INCORRECT_MESSAGES, STREAK_MESSAGES are module-level constants - stable references
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAnswers, currentQuestion, quiz, streak, maxStreak, answeredQuestions]);

  const handleNext = () => {
    setShowFeedback(false);
    setStreakMessage('');
    if (currentQuestion < quiz.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    setShowFeedback(false);
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    // Vérifier que toutes les questions sont répondues
    if (selectedAnswers.includes(-1)) {
      toast({
        title: 'Quiz incomplet',
        description: 'Veuillez répondre à toutes les questions',
        variant: 'destructive'
      });
      return;
    }

    setSubmitting(true);
    try {
      const response = await quizAPI.submit(quiz.id, selectedAnswers);
      
      // Map backend fields to frontend expected fields and include quiz questions for review
      const enhancedResults = {
        score: response.data.score,
        passed: response.data.passed,
        correctCount: response.data.correct,
        totalQuestions: response.data.total,
        pointsEarned: response.data.pointsEarned || 0,
        questions: quiz.questions,
        bonusPoints,
        maxStreak,
        totalPointsEarned: (response.data.pointsEarned || 0) + bonusPoints
      };
      
      setResults(enhancedResults);
      
      if (response.data.passed && user) {
        updateUser({
          ...user,
          points: user.points + enhancedResults.totalPointsEarned
        });
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Erreur lors de la soumission',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex items-center justify-center">
        <div className="text-center">
          <Flower2 className="w-16 h-16 text-pink-300 mx-auto mb-4 animate-pulse" />
          <div className="text-white text-xl">Préparation du quiz...</div>
        </div>
      </div>
    );
  }

  if (!quiz) return null;

  // Écran de résultats amélioré
  if (results) {
    const isPerfect = results.score === 100;
    const isExcellent = results.score >= 80;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 pb-24">
        <div className="sticky top-0 bg-purple-900/80 backdrop-blur-md border-b border-white/10 px-4 py-4 z-40">
          <div className="flex items-center gap-3">
            <Flower2 className="w-8 h-8 text-pink-300" />
            <h1 className="text-xl font-bold text-white">Résultats</h1>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
          {/* Carte principale de résultat */}
          <Card className={`p-8 backdrop-blur-md border-white/20 text-center relative overflow-hidden ${
            isPerfect ? 'bg-gradient-to-br from-yellow-500/30 to-amber-500/30 border-yellow-400/50' :
            isExcellent ? 'bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-green-400/30' :
            results.passed ? 'bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border-blue-400/30' :
            'bg-gradient-to-br from-orange-500/20 to-yellow-500/20 border-orange-400/30'
          }`}>
            {/* Animation de confettis pour score parfait */}
            {isPerfect && (
              <div className="absolute inset-0 pointer-events-none">
                <Sparkles className="absolute top-4 left-4 w-8 h-8 text-yellow-300 animate-pulse" />
                <Star className="absolute top-8 right-8 w-6 h-6 text-yellow-400 animate-bounce" />
                <Sparkles className="absolute bottom-8 left-8 w-6 h-6 text-yellow-300 animate-pulse" />
                <Star className="absolute bottom-4 right-4 w-8 h-8 text-yellow-400 animate-bounce" />
              </div>
            )}
            
            {isPerfect ? (
              <Trophy className="w-24 h-24 text-yellow-400 mx-auto mb-4 animate-bounce" />
            ) : isExcellent ? (
              <Award className="w-20 h-20 text-green-400 mx-auto mb-4" />
            ) : results.passed ? (
              <CheckCircle className="w-20 h-20 text-blue-400 mx-auto mb-4" />
            ) : (
              <Heart className="w-20 h-20 text-pink-400 mx-auto mb-4" />
            )}
            
            <h2 className="text-3xl font-bold text-white mb-2">
              {isPerfect ? '🏆 PARFAIT ! 🏆' :
               isExcellent ? 'Excellent ! 🎉' :
               results.passed ? 'Bien joué ! 👏' :
               'Continue d\'apprendre ! 💪'}
            </h2>
            
            <div className="text-6xl font-bold text-white my-6">
              {results.score}%
            </div>
            
            <p className="text-pink-100 text-lg mb-4">
              {results.correctCount} / {results.totalQuestions} réponses correctes
            </p>
            
            {/* Statistiques de streak */}
            {maxStreak >= 3 && (
              <div className="bg-orange-500/20 border border-orange-400/30 rounded-lg p-3 mb-4 flex items-center justify-center gap-2">
                <Flame className="w-6 h-6 text-orange-400" />
                <span className="text-orange-100 font-semibold">
                  Meilleure série : {maxStreak} réponses d'affilée !
                </span>
              </div>
            )}
            
            {/* Points gagnés */}
            {results.passed && (
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-center gap-2">
                  <Star className="w-6 h-6 text-yellow-400" />
                  <span className="text-white font-semibold">Points gagnés</span>
                </div>
                <div className="text-2xl font-bold text-yellow-300">
                  +{results.totalPointsEarned} points
                </div>
                {bonusPoints > 0 && (
                  <p className="text-sm text-yellow-200/80">
                    (dont {bonusPoints} bonus de série 🔥)
                  </p>
                )}
              </div>
            )}
          </Card>

          {/* Message motivant */}
          <Card className="p-4 bg-gradient-to-r from-pink-500/20 to-purple-500/20 border-pink-400/30">
            <p className="text-pink-100 text-center">
              {isPerfect ? "Tu es une vraie experte ! Ton savoir peut aider d'autres filles autour de toi. 🌟" :
               isExcellent ? "Tu maîtrises vraiment bien ce sujet ! Continue sur cette lancée. ✨" :
               results.passed ? "Tu progresses bien ! Chaque leçon te rend plus forte. 💪" :
               "L'apprentissage prend du temps. Relis la leçon et réessaie, tu vas y arriver ! 🌸"}
            </p>
          </Card>

          {/* Révision des réponses */}
          <div className="space-y-4">
            <h3 className="text-2xl font-bold text-white flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-pink-300" />
              Révision
            </h3>
            {results.questions.map((question, qIndex) => (
              <Card key={question.id || `q-${qIndex}`} className="p-6 bg-white/10 backdrop-blur-md border-white/20">
                <div className="flex items-start gap-2 mb-4">
                  {question.options[selectedAnswers[qIndex]]?.isCorrect ? (
                    <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  ) : (
                    <XCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" />
                  )}
                  <h4 className="text-white font-semibold">
                    {question.question}
                  </h4>
                </div>
                
                <div className="space-y-2 mb-4">
                  {question.options.map((option, oIndex) => {
                    const isSelected = selectedAnswers[qIndex] === oIndex;
                    const isCorrect = option.isCorrect;
                    let className = 'p-3 rounded-lg border-2 ';
                    if (isCorrect) {
                      className += 'border-green-400 bg-green-500/20 text-green-100';
                    } else if (isSelected) {
                      className += 'border-red-400 bg-red-500/20 text-red-100';
                    } else {
                      className += 'border-white/20 bg-white/5 text-pink-200';
                    }
                    
                    return (
                      <div key={option.id || `${question.id || qIndex}-opt-${oIndex}`} className={className}>
                        <div className="flex items-center gap-2">
                          {isCorrect && <CheckCircle className="w-5 h-5 text-green-400" />}
                          {!isCorrect && isSelected && <XCircle className="w-5 h-5 text-red-400" />}
                          <span>{option.text}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <div className="bg-blue-500/20 border border-blue-400/30 rounded-lg p-3">
                  <p className="text-blue-100 text-sm">
                    <strong>💡 Explication :</strong> {question.explanation}
                  </p>
                </div>
              </Card>
            ))}
          </div>

          {/* Boutons d'action */}
          <div className="flex gap-3">
            <Button 
              onClick={() => lesson && navigate(`/module/${lesson.moduleId}`)}
              className="flex-1 bg-white/10 hover:bg-white/20 text-white py-6 rounded-xl"
            >
              Retour au module
            </Button>
            {!results.passed && (
              <Button 
                onClick={() => {
                  setResults(null);
                  setCurrentQuestion(0);
                  setSelectedAnswers(new Array(quiz.questions.length).fill(-1));
                  setAnsweredQuestions(new Array(quiz.questions.length).fill(false));
                  setStreak(0);
                  setMaxStreak(0);
                  setBonusPoints(0);
                  setShowFeedback(false);
                }}
                className="flex-1 bg-gradient-to-r from-pink-400 to-pink-600 hover:from-pink-500 hover:to-pink-700 text-white py-6 rounded-xl"
              >
                Réessayer 🔄
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const question = quiz.questions[currentQuestion];
  const progress = ((currentQuestion + 1) / quiz.questions.length) * 100;
  const isQuestionAnswered = answeredQuestions[currentQuestion];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 pb-24">
      {/* Header avec streak */}
      <div className="sticky top-0 bg-purple-900/80 backdrop-blur-md border-b border-white/10 px-4 py-4 z-40">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(`/lesson/${lessonId}`)} className="text-pink-300">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <Flower2 className="w-8 h-8 text-pink-300" />
            <h1 className="text-xl font-bold text-white">Quiz</h1>
          </div>
          
          {/* Indicateur de streak */}
          {streak >= 2 && (
            <div className="flex items-center gap-2 bg-orange-500/30 px-3 py-1 rounded-full animate-pulse">
              <Flame className="w-5 h-5 text-orange-400" />
              <span className="text-orange-100 font-bold">{streak}</span>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Barre de progression améliorée */}
        <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20 mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-white font-semibold">
              Question {currentQuestion + 1} / {quiz.questions.length}
            </span>
            <div className="flex items-center gap-2">
              {bonusPoints > 0 && (
                <span className="text-yellow-300 text-sm">+{bonusPoints} bonus</span>
              )}
              <span className="text-pink-300">{Math.round(progress)}%</span>
            </div>
          </div>
          <div className="h-3 bg-purple-900/50 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-pink-400 via-purple-500 to-pink-400 transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          
          {/* Indicateurs de questions */}
          <div className="flex justify-center gap-2 mt-3">
            {quiz.questions.map((q, idx) => (
              <div 
                key={q.id || `dot-${idx}`}
                className={`w-3 h-3 rounded-full transition-all ${
                  idx === currentQuestion ? 'w-6 bg-pink-400' :
                  answeredQuestions[idx] ? 
                    (quiz.questions[idx].options[selectedAnswers[idx]]?.isCorrect ? 'bg-green-400' : 'bg-red-400') :
                    'bg-white/30'
                }`}
              />
            ))}
          </div>
        </Card>

        {/* Message de streak */}
        {streakMessage && (
          <div className="mb-4 p-3 bg-gradient-to-r from-orange-500/30 to-yellow-500/30 border border-orange-400/50 rounded-xl text-center animate-bounce">
            <span className="text-orange-100 font-bold text-lg">{streakMessage}</span>
          </div>
        )}

        {/* Carte de question */}
        <Card className="p-6 bg-white/10 backdrop-blur-md border-white/20 mb-6 relative overflow-hidden">
          {/* Badge de type de question */}
          {question.type === 'true_false' && (
            <div className="absolute top-2 right-2 bg-blue-500/30 text-blue-200 text-xs px-2 py-1 rounded-full">
              Vrai ou Faux
            </div>
          )}
          
          <h2 className="text-xl md:text-2xl font-bold text-white mb-6 pr-16">{question.question}</h2>
          
          <div className="space-y-3">
            {question.options.map((option, index) => {
              const isSelected = selectedAnswers[currentQuestion] === index;
              const isCorrect = option.isCorrect;
              
              let buttonClass = 'w-full p-4 rounded-xl text-left transition-all ';
              
              if (isQuestionAnswered) {
                if (isCorrect) {
                  buttonClass += 'bg-green-500/30 border-2 border-green-400 text-green-100';
                } else if (isSelected) {
                  buttonClass += 'bg-red-500/30 border-2 border-red-400 text-red-100';
                } else {
                  buttonClass += 'bg-white/5 border-2 border-white/10 text-pink-200/50';
                }
              } else {
                if (isSelected) {
                  buttonClass += 'bg-gradient-to-r from-pink-500 to-purple-600 text-white border-2 border-pink-300 scale-[1.02]';
                } else {
                  buttonClass += 'bg-white/5 hover:bg-white/10 text-pink-100 border-2 border-white/20 hover:border-pink-300/50';
                }
              }
              
              return (
                <button
                  key={option.id || `opt-${currentQuestion}-${index}`}
                  onClick={() => handleSelectAnswer(index)}
                  disabled={isQuestionAnswered}
                  className={buttonClass}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      isQuestionAnswered && isCorrect ? 'border-green-400 bg-green-400' :
                      isQuestionAnswered && isSelected && !isCorrect ? 'border-red-400 bg-red-400' :
                      isSelected ? 'border-white bg-white' : 'border-pink-300'
                    }`}>
                      {isQuestionAnswered && isCorrect && <CheckCircle className="w-5 h-5 text-white" />}
                      {isQuestionAnswered && isSelected && !isCorrect && <XCircle className="w-5 h-5 text-white" />}
                      {!isQuestionAnswered && isSelected && <div className="w-3 h-3 rounded-full bg-pink-500" />}
                    </div>
                    <span className="font-medium">{option.text}</span>
                  </div>
                </button>
              );
            })}
          </div>
          
          {/* Feedback après réponse */}
          {showFeedback && (
            <div className={`mt-6 p-4 rounded-xl ${
              lastAnswerCorrect 
                ? 'bg-green-500/20 border border-green-400/50' 
                : 'bg-orange-500/20 border border-orange-400/50'
            }`}>
              <div className="flex items-center gap-3 mb-2">
                {lastAnswerCorrect ? (
                  <CheckCircle className="w-6 h-6 text-green-400" />
                ) : (
                  <Heart className="w-6 h-6 text-orange-400" />
                )}
                <span className={`font-bold ${lastAnswerCorrect ? 'text-green-100' : 'text-orange-100'}`}>
                  {feedbackMessage}
                </span>
              </div>
              <p className="text-blue-100 text-sm bg-blue-500/20 p-3 rounded-lg">
                <strong>💡</strong> {question.explanation}
              </p>
            </div>
          )}
        </Card>

        {/* Boutons de navigation */}
        <div className="flex gap-3">
          <Button
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
            className="flex-1 bg-white/10 hover:bg-white/20 text-white py-6 rounded-xl disabled:opacity-30"
          >
            Précédent
          </Button>
          
          {!isQuestionAnswered ? (
            <Button
              onClick={handleValidateAnswer}
              disabled={selectedAnswers[currentQuestion] === -1}
              className="flex-1 bg-gradient-to-r from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white py-6 rounded-xl disabled:opacity-50"
            >
              Valider ✓
            </Button>
          ) : currentQuestion < quiz.questions.length - 1 ? (
            <Button
              onClick={handleNext}
              className="flex-1 bg-gradient-to-r from-pink-400 to-pink-600 hover:from-pink-500 hover:to-pink-700 text-white py-6 rounded-xl"
            >
              Suivant →
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 bg-gradient-to-r from-green-400 to-emerald-500 hover:from-green-500 hover:to-emerald-600 text-white py-6 rounded-xl disabled:opacity-50"
            >
              {submitting ? 'Envoi...' : 'Voir mes résultats 🎉'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LessonQuizEnhanced;
